/* Paleta de comandos (⌘K).
 *
 * Substitui o <select> de 29 UUIDs do painel antigo. Um seletor nativo com
 * identificadores opacos e ilegivel: aqui da para buscar por prefixo do id, por
 * posicao no ranking de massa, ou pelo nome da secao. */

import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { compact, shortId } from "../lib/format";
import { useUsers } from "./Shell";

interface Item { id: string; label: string; hint?: string; run: () => void; }

const ROUTES: [string, string][] = [
  ["/visao-geral", "Visão geral"],
  ["/categorias", "Análise por categoria"],
  ["/relatorios", "Relatórios dos agentes"],
  // A primeira sessão da aba é Perfil, então `/margin-finder` sem parâmetro abre nela.
  // Esta lista é estática de propósito: os agentes descobertos variam, e a paleta não
  // pode depender de uma consulta para abrir.
  ["/margin-finder", "Margin Finder · perfil"],
  ["/margin-finder?sessao=telecom", "Margin Finder · telecom"],
  ["/margin-finder?sessao=seguros", "Margin Finder · seguros"],
  ["/margin-finder?sessao=saude", "Margin Finder · saúde"],
  ["/margin-finder?sessao=assinaturas", "Margin Finder · assinaturas"],
  ["/user-statistics", "User statistics · usuários e transações por dia"],
  ["/qualidade", "Qualidade dos dados"],
  ["/fontes", "Fontes de dados"],
  ["/guia", "Guia do grafo"],
];

export function CommandPalette({ onClose }: { onClose: () => void }) {
  const [query, setQuery] = useState("");
  const [index, setIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { data: users } = useUsers();

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Ao trocar de usuario, fica na mesma secao em vez de voltar para o inicio.
  const section = useMemo(() => {
    const first = location.pathname.split("/")[1];
    return ["usuarios", "ontologia", "grafo"].includes(first)
      ? first : "usuarios";
  }, [location.pathname]);

  const items = useMemo<Item[]>(() => {
    const out: Item[] = [];
    for (const [to, label] of ROUTES) {
      out.push({ id: to, label, hint: "seção", run: () => navigate(to) });
    }
    for (const [rank, user] of (users ?? []).entries()) {
      out.push({
        id: user.user_id,
        label: user.is_aggregate
          ? "Todos os usuários (agregado)"
          : `${shortId(user.user_id)}…`,
        hint: user.is_aggregate
          ? "soma de todos"
          : `#${rank} · ${user.months} meses · R$ ${compact(user.total)}`,
        run: () => navigate(`/${section}/${user.user_id}`),
      });
    }
    const needle = query.trim().toLowerCase();
    if (!needle) return out.slice(0, 40);
    return out.filter((item) =>
      item.label.toLowerCase().includes(needle) ||
      item.id.toLowerCase().includes(needle) ||
      (item.hint ?? "").toLowerCase().includes(needle)).slice(0, 40);
  }, [users, query, navigate, section]);

  useEffect(() => { setIndex(0); }, [query]);

  const onKey = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") return onClose();
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setIndex((i) => Math.min(i + 1, items.length - 1));
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setIndex((i) => Math.max(i - 1, 0));
    }
    if (event.key === "Enter" && items[index]) {
      items[index].run();
      onClose();
    }
  };

  return (
    <div className="palette-backdrop" onClick={onClose} role="presentation">
      <div className="palette" onClick={(e) => e.stopPropagation()} role="dialog"
           aria-modal="true" aria-label="Ir para">
        <input ref={inputRef} type="text" value={query} onKeyDown={onKey}
               placeholder="Buscar usuário, seção…"
               onChange={(e) => setQuery(e.target.value)}
               aria-label="Buscar" />
        <ul role="listbox">
          {items.map((item, i) => (
            <li key={item.id} role="option" aria-selected={i === index}
                onMouseEnter={() => setIndex(i)}
                onClick={() => { item.run(); onClose(); }}>
              {item.label}
              {item.hint && <span className="hint">{item.hint}</span>}
            </li>
          ))}
          {!items.length && (
            <li aria-disabled style={{ color: "var(--ink-3)" }}>Nada encontrado.</li>
          )}
        </ul>
      </div>
    </div>
  );
}
